import {
  atDurationBound,
  formatDurationRange,
} from "@/lib/provider-service-duration";

import type { ServiceTiny } from "@/types/service";

type ServiceTiming = Pick<
  ServiceTiny,
  "duration" | "prepTimeDuration" | "prepTimeBuffer" | "prepTimeRequiresPatient"
>;

/**
 * Return the consolidated prep window during which the patient must be at the
 * clinic. Scheduling consolidates prep using the maximum prep duration and
 * maximum buffer rather than adding each service's prep independently.
 *
 * If every configured prep is provider-only, none of that window is part of
 * the patient-facing appointment duration.
 */
export const computePatientRequiredPrepTime = (
  services?: ServiceTiming[] | null,
): number => {
  if (!services?.length) return 0;

  const servicesWithPrep = services.filter(
    (service) => (service.prepTimeDuration ?? 0) > 0,
  );
  const maxBuffer = Math.max(
    0,
    ...services.map((service) => service.prepTimeBuffer ?? 0),
  );
  // Scheduling treats a buffer-only configuration as patient attendance
  // before the first service even though there is no explicit prep segment.
  if (servicesWithPrep.length === 0) return maxBuffer;

  const prepRequiresPatient = servicesWithPrep.some(
    (service) => service.prepTimeRequiresPatient !== false,
  );
  if (!prepRequiresPatient) return 0;

  const maxDuration = Math.max(
    0,
    ...servicesWithPrep.map((service) => service.prepTimeDuration ?? 0),
  );
  return maxDuration + maxBuffer;
};

/** The amount of time the patient should expect to be at the clinic. */
export const computePatientFacingDuration = (
  services?: ServiceTiming[] | null,
): number => {
  if (!services?.length) return 0;

  return (
    services.reduce((total, service) => total + service.duration, 0) +
    computePatientRequiredPrepTime(services)
  );
};

/**
 * Minutes a service's selected add-ons add to the appointment. A service with
 * `addonsUseLongestDuration` runs them side by side, so only the longest counts.
 */
export const computeAddonMinutes = (
  service: { addonsUseLongestDuration?: boolean },
  addonDurations: number[],
): number =>
  service.addonsUseLongestDuration
    ? Math.max(0, ...addonDurations)
    : addonDurations.reduce((total, duration) => total + duration, 0);

/** The patient-facing time for one service and the add-ons selected for it. */
export const computePatientFacingDurationWithAddons = (
  service: ServiceTiming & { addonsUseLongestDuration?: boolean },
  addonServices: ServiceTiming[],
): number =>
  service.duration +
  computeAddonMinutes(
    service,
    addonServices.map((addon) => addon.duration),
  ) +
  computePatientRequiredPrepTime([service, ...addonServices]);

/** "30" or "30–45" when providers take different times, for one service. */
export const patientFacingDurationLabel = (
  service: ServiceTiming & Pick<ServiceTiny, "providerDurations">,
): string =>
  formatDurationRange(
    computePatientFacingDuration([atDurationBound(service, "min")]),
    computePatientFacingDuration([atDurationBound(service, "max")]),
  );
